(function () {
  function money(cents, currency) {
    const amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'EUR' }).format(amount);
    } catch (error) {
      return amount.toFixed(2);
    }
  }

  class EbMainProduct extends HTMLElement {
    connectedCallback() {
      this.product = JSON.parse(this.querySelector('[data-eb-product-json]')?.textContent || '{}');
      this.form = this.querySelector('[data-eb-product-form]');
      this.variantSelect = this.querySelector('[data-eb-variant-select]');
      this.quantityInput = this.querySelector('[data-eb-quantity]');
      this.atc = this.querySelector('[data-eb-atc]');
      this.atcLabel = this.querySelector('[data-eb-atc-label]');
      this.totalNode = this.querySelector('[data-eb-total]');
      this.status = this.querySelector('[data-eb-form-status]');
      this.currency = this.dataset.currency || 'EUR';
      this.selectedVariant = this.currentVariant();
      this.isAddingToCart = false;

      this.bindOptions();
      this.bindGallery();
      this.bindTrust();
      this.bindQuantity();
      this.bindForm();
      this.bindDrawers();
      this.bindBundle();
      this.bindWarranty();
      this.bindTestRide();
      this.bindShipping();
      this.bindDeepAccordions();
      this.bindSizeFit();
      this.bindStickyAtc();
      this.observeSpecGrid();
      this.updateTotals();
    }

    disconnectedCallback() {
      this.clearAtcTimer();
      if (this.boundDrawerCloseCapture) {
        document.removeEventListener('click', this.boundDrawerCloseCapture, true);
        this.boundDrawerCloseCapture = null;
      }
    }

    currentVariant() {
      const id = Number(this.variantSelect?.value);
      return this.product.variants?.find((variant) => Number(variant.id) === id) || this.product.variants?.[0] || null;
    }

    selectedOptions() {
      const optionInputs = [...this.querySelectorAll('[data-eb-option-input]:checked')];
      return optionInputs
        .sort((a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex))
        .map((input) => input.value);
    }

    bindOptions() {
      this.addEventListener('change', (event) => {
        if (!event.target.matches('[data-eb-option-input]')) return;
        const options = this.selectedOptions();
        const variant = this.product.variants?.find((item) => item.options.every((option, index) => option === options[index]));
        if (!variant) {
          this.restoreOptionInputs();
          return;
        }
        this.selectVariant(variant);
      });
    }

    restoreOptionInputs() {
      const current = this.selectedVariant;
      if (!current) return;

      this.querySelectorAll('[data-eb-option-input]').forEach((input) => {
        const index = Number(input.dataset.optionIndex);
        if (Number.isNaN(index)) return;
        if (input.value === current.options[index]) input.checked = true;
      });

      this.querySelectorAll('[data-eb-selected-option]').forEach((node) => {
        const position = Number(node.dataset.ebSelectedOption) - 1;
        node.textContent = current.options[position] || '';
      });
    }

    selectVariant(variant) {
      this.selectedVariant = variant;
      if (this.variantSelect) this.variantSelect.value = variant.id;
      this.querySelectorAll('[data-eb-selected-option]').forEach((node) => {
        const position = Number(node.dataset.ebSelectedOption) - 1;
        node.textContent = variant.options[position] || '';
      });
      if (this.atc) {
        this.clearAtcTimer();
        this.isAddingToCart = false;
        this.setAtcIdle();
      }
      this.updateTotals();
      this.updateMedia(variant.featured_media?.id);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
      this.dispatchEvent(new CustomEvent('eb:variant:change', { bubbles: true, detail: { variant } }));
    }

    bindGallery() {
      const slides = Array.from(this.querySelectorAll('[data-media-id]'));
      this.galleryOrder = slides.map((s) => s.dataset.mediaId);

      const prev = this.querySelector('[data-eb-media-prev]');
      const next = this.querySelector('[data-eb-media-next]');
      if (prev) prev.addEventListener('click', () => this.stepMedia(-1));
      if (next) next.addEventListener('click', () => this.stepMedia(1));

      this.querySelectorAll('[data-eb-media-dot]').forEach((dot) => {
        dot.addEventListener('click', () => this.updateMedia(dot.dataset.ebMediaDot));
      });

      const btn360 = this.querySelector('[data-eb-media-360]');
      if (btn360) {
        btn360.addEventListener('click', () => {
          const model = this.querySelector('[data-media-type="model"]');
          if (model) this.updateMedia(model.dataset.mediaId);
        });
      }
    }

    currentMediaIndex() {
      const active = this.querySelector('[data-media-id].is-active');
      const id = active ? active.dataset.mediaId : (this.galleryOrder || [])[0];
      return (this.galleryOrder || []).indexOf(String(id));
    }

    stepMedia(direction) {
      const order = this.galleryOrder || [];
      if (!order.length) return;
      let i = this.currentMediaIndex();
      if (i < 0) i = 0;
      const nextIndex = (i + direction + order.length) % order.length;
      this.updateMedia(order[nextIndex]);
    }

    updateMedia(mediaId) {
      if (!mediaId) return;
      this.querySelectorAll('[data-media-id]').forEach((media) => {
        const active = String(media.dataset.mediaId) === String(mediaId);
        media.hidden = !active && window.matchMedia('(min-width: 1024px)').matches;
        media.classList.toggle('is-active', active);
      });
      this.querySelectorAll('[data-eb-media-dot]').forEach((dot) => {
        dot.classList.toggle('is-active', String(dot.dataset.ebMediaDot) === String(mediaId));
      });
      const order = this.galleryOrder || [];
      const idx = order.indexOf(String(mediaId));
      const counter = this.querySelector('[data-eb-media-index]');
      if (counter && idx >= 0) counter.textContent = idx + 1;
    }

    bindTrust() {
      const trust = this.querySelector('eb-trust');
      if (!trust) return;
      const descBox = trust.querySelector('[data-eb-trust-desc]');
      if (!descBox) return;
      let openIndex = -1;
      trust.querySelectorAll('[data-eb-trust-toggle]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const idx = btn.dataset.ebTrustToggle;
          trust.querySelectorAll('[data-eb-trust-toggle]').forEach((b) => b.classList.remove('is-open'));
          if (String(openIndex) === String(idx)) {
            descBox.hidden = true;
            descBox.classList.remove('is-open');
            openIndex = -1;
            return;
          }
          const tpl = trust.querySelector(`[data-eb-trust-content="${idx}"]`);
          descBox.innerHTML = tpl ? tpl.innerHTML : '';
          descBox.hidden = false;
          requestAnimationFrame(() => descBox.classList.add('is-open'));
          btn.classList.add('is-open');
          openIndex = idx;
        });
      });
    }

    bindTestRide() {
      const root = this.querySelector('[data-eb-test-ride]');
      if (!root) return;
      const search = root.querySelector('[data-eb-tr-search]');
      const storeSearch = root.querySelector('[data-eb-tr-store-search]');
      const country = root.querySelector('[data-eb-tr-country]');
      const categoryInputs = [...root.querySelectorAll('[data-eb-tr-category]')];
      const modelInputs = [...root.querySelectorAll('[data-eb-tr-model]')];
      const stores = [...root.querySelectorAll('[data-eb-tr-store]')];

      const selectedValues = (inputs) => inputs.filter((input) => input.checked).map((input) => input.value);
      const includesAny = (haystack, needles) => !needles.length || needles.some((needle) => haystack.split(/\s+/).includes(needle));
      const applyFilters = () => {
        const q = search?.value.trim().toLowerCase() || '';
        const storeQ = storeSearch?.value.trim().toLowerCase() || '';
        const countryValue = country?.value || '';
        const categories = selectedValues(categoryInputs);
        const models = selectedValues(modelInputs);

        stores.forEach((store) => {
          const hay = store.dataset.search || '';
          const storeName = store.dataset.storeName || '';
          const serviceKeys = store.dataset.services || '';
          const modelKeys = store.dataset.models || '';
          const allModels = store.dataset.allModels === 'true';
          const matchesText = q === '' || hay.includes(q);
          const matchesStore = storeQ === '' || storeName.includes(storeQ);
          const matchesCountry = countryValue === '' || store.dataset.country === countryValue;
          const matchesCategory = includesAny(serviceKeys, categories);
          const matchesModel = !models.length || allModels || includesAny(modelKeys, models);
          store.hidden = !(matchesText && matchesStore && matchesCountry && matchesCategory && matchesModel);
        });
      };

      search?.addEventListener('input', applyFilters);
      storeSearch?.addEventListener('input', applyFilters);
      country?.addEventListener('change', applyFilters);
      categoryInputs.forEach((input) => input.addEventListener('change', applyFilters));
      modelInputs.forEach((input) => input.addEventListener('change', applyFilters));

      const locate = root.querySelector('[data-eb-tr-locate]');
      if (locate && navigator.geolocation) {
        locate.addEventListener('click', () => {
          navigator.geolocation.getCurrentPosition((pos) => {
            this.sortStoresByDistance(root, pos.coords.latitude, pos.coords.longitude);
          });
        });
      }

      applyFilters();
    }

    sortStoresByDistance(root, lat, lng) {
      const list = root.querySelector('[data-eb-tr-list]');
      const stores = [...root.querySelectorAll('[data-eb-tr-store]')];
      const dist = (a, b, c, d) => {
        const dx = a - c;
        const dy = b - d;
        return dx * dx + dy * dy;
      };
      stores
        .map((s) => ({ el: s, d: dist(parseFloat(s.dataset.lat), parseFloat(s.dataset.lng), lat, lng) }))
        .sort((x, y) => x.d - y.d)
        .forEach((x) => list.appendChild(x.el));
      if (this.trMap) this.trMap.setView([lat, lng], 8);
    }

    initTestRideMap() {
      const root = this.querySelector('[data-eb-test-ride]');
      if (!root || this.trMap) return;
      const mapEl = root.querySelector('[data-eb-tr-map]');
      const fallback = root.querySelector('[data-eb-tr-fallback]');
      const stores = [...root.querySelectorAll('[data-eb-tr-store]')]
        .map((s) => ({ lat: parseFloat(s.dataset.lat), lng: parseFloat(s.dataset.lng), name: s.dataset.name }))
        .filter((s) => !Number.isNaN(s.lat) && !Number.isNaN(s.lng));
      if (!mapEl || !stores.length) return;

      const boot = () => {
        if (!window.L) return;
        if (fallback) fallback.remove();
        this.trMap = window.L.map(mapEl, { scrollWheelZoom: false }).setView([stores[0].lat, stores[0].lng], 6);
        window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 18, attribution: '© OpenStreetMap' }).addTo(this.trMap);
        const bounds = [];
        stores.forEach((s) => {
          window.L.marker([s.lat, s.lng]).addTo(this.trMap).bindPopup(`<strong>${s.name}</strong>`);
          bounds.push([s.lat, s.lng]);
        });
        if (bounds.length > 1) this.trMap.fitBounds(bounds, { padding: [30, 30] });
        setTimeout(() => this.trMap.invalidateSize(), 60);
      };

      if (window.L) { boot(); return; }
      const css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.css';
      document.head.appendChild(css);
      const js = document.createElement('script');
      js.src = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.js';
      js.onload = boot;
      js.onerror = () => { if (fallback) fallback.querySelector('span').textContent = 'Map unavailable'; };
      document.head.appendChild(js);
    }

    initCompare() {
      const root = this.querySelector('[data-eb-compare]');
      if (!root || root.dataset.ready) return;
      const dataEl = root.querySelector('[data-eb-compare-data]');
      let data;
      try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }
      root.dataset.ready = '1';

      const products = data.products || [];
      const tabs = data.tabs || [];
      let curTab = 0;
      let diffOnly = false;

      const modelsEl = root.querySelector('[data-eb-compare-models]');
      const tabsEl = root.querySelector('[data-eb-compare-tabs]');
      const rowsEl = root.querySelector('[data-eb-compare-rows]');
      const diffInput = root.querySelector('[data-eb-compare-diff]');
      const diffLabel = root.querySelector('[data-eb-compare-diff-label]');
      if (diffLabel) diffLabel.textContent = data.diffLabel || 'Differences only';

      const cols = `repeat(${products.length}, minmax(0, 1fr))`;

      // model heads
      modelsEl.style.gridTemplateColumns = cols;
      modelsEl.innerHTML = products.map((p) => {
        const isThis = p.handle === data.thisHandle;
        const img = p.image ? `<img src="${p.image}" alt="" loading="lazy">` : '';
        const swatches = (p.swatches || []).filter(Boolean).slice(0, 3).map((c) => `<span style="background:${c}"></span>`).join('');
        const name = (p.specs && p.specs.model) || p.model || p.title || '';
        return `<div class="eb-compare__mcol${isThis ? ' is-this' : ''}">
          <div class="eb-compare__mimg">${img}</div>
          <div class="eb-compare__mname">${name}</div>
          ${isThis ? `<span class="eb-compare__this">${data.thisLabel || 'THIS'}</span>` : ''}
          ${swatches ? `<div class="eb-compare__msw">${swatches}</div>` : ''}
          <div class="eb-compare__mprice">${p.priceText || ''}</div>
        </div>`;
      }).join('');

      // tabs
      tabsEl.innerHTML = tabs.map((t, i) => `<button type="button" class="eb-compare__tab${i === 0 ? ' is-on' : ''}" data-tab="${i}">${t.label}</button>`).join('');
      tabsEl.querySelectorAll('[data-tab]').forEach((btn) => {
        btn.addEventListener('click', () => {
          curTab = parseInt(btn.dataset.tab, 10);
          tabsEl.querySelectorAll('[data-tab]').forEach((b) => b.classList.toggle('is-on', b === btn));
          renderRows();
        });
      });

      if (diffInput) diffInput.addEventListener('change', () => { diffOnly = diffInput.checked; renderRows(); });

      const specOf = (p, key) => ((p.specs && p.specs[key]) || '').toString().trim();
      const withUnit = (value, unit) => {
        const normalized = String(value || '').trim();
        const suffix = String(unit || '').trim();
        if (!normalized || !suffix) return normalized;
        if (normalized.toLowerCase().endsWith(suffix.toLowerCase())) return normalized;
        return `${normalized} ${suffix}`;
      };
      const numeric = (v) => {
        const m = String(v || '').match(/-?\d+(?:\.\d+)?/);
        return m ? parseFloat(m[0]) : null;
      };

      const renderRows = () => {
        const tab = tabs[curTab];
        const rowsHtml = (tab.rows || []).map((row) => {
          const vals = products.map((p) => specOf(p, row.key));
          const allSame = vals.every((v) => v === vals[0]);
          if (diffOnly && allSame) return '';
          let bestIdx = -1;
          if (row.better === 'high' || row.better === 'low') {
            const nums = vals.map(numeric);
            const valid = nums.filter((n) => n !== null);
            if (valid.length && !nums.every((n) => n === nums[0])) {
              const target = row.better === 'high' ? Math.max(...valid) : Math.min(...valid);
              bestIdx = nums.indexOf(target);
            }
          }
          const cells = vals.map((v, i) => {
            const best = i === bestIdx;
            const display = v ? withUnit(v, row.unit) : '—';
            return `<div class="eb-compare__cell${best ? ' is-best' : ''}">
              <span class="eb-compare__cell-label">${row.label}</span>
              <span class="eb-compare__cell-value">${display}${best ? ' <span class="eb-compare__tick">✓</span>' : ''}</span>
            </div>`;
          }).join('');
          return `<div class="eb-compare__row" style="grid-template-columns:${cols}">${cells}</div>`;
        }).join('');
        rowsEl.innerHTML = rowsHtml || `<div class="eb-compare__empty">No differences in this category</div>`;
      };

      renderRows();
    }

    bindDeepAccordions() {
      this.querySelectorAll('[data-eb-accordion]').forEach((acc) => {
        const trigger = acc.querySelector('[data-eb-accordion-toggle]');
        const panel = acc.querySelector('[data-eb-accordion-panel]');
        if (!trigger || !panel) return;
        trigger.addEventListener('click', () => {
          const open = acc.classList.toggle('is-open');
          trigger.setAttribute('aria-expanded', String(open));
          panel.hidden = !open;
        });
      });
    }

    bindShipping() {
      const inv = this.querySelector('eb-inventory[data-country]');
      if (!inv) return;
      const dataEl = inv.querySelector('[data-eb-warehouses]');
      const shipEl = inv.querySelector('[data-eb-ship]');
      const textEl = inv.querySelector('[data-eb-ship-text]');
      if (!dataEl || !shipEl || !textEl) return;

      let warehouses = [];
      try { warehouses = JSON.parse(dataEl.textContent) || []; } catch (e) { return; }
      if (!warehouses.length) return;

      const render = (country) => {
        const cc = (country || '').toUpperCase();
        // pick warehouse: region match first, then default, then first
        let wh = warehouses.find((w) => (w.regions || []).map((r) => r.toUpperCase()).includes(cc));
        const isLocal = !!wh;
        if (!wh) wh = warehouses.find((w) => w.is_default) || warehouses[0];

        const processing = parseInt(wh.processing, 10) || 1;
        const transit = isLocal ? (parseInt(wh.transit_local, 10) || 3) : (parseInt(wh.transit_intl, 10) || 7);
        const addBusinessDays = (date, days) => {
          const d = new Date(date);
          let added = 0;
          while (added < days) {
            d.setDate(d.getDate() + 1);
            const day = d.getDay();
            if (day !== 0 && day !== 6) added += 1;
          }
          return d;
        };
        const start = addBusinessDays(new Date(), processing);
        const lo = addBusinessDays(start, Math.max(1, transit - 1));
        const hi = addBusinessDays(start, transit + 1);
        const fmt = (d) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
        const label = this.dataset.arrivesLabel || 'Arrives';
        textEl.textContent = `${label} ${fmt(lo)} – ${fmt(hi)}`;
        shipEl.hidden = false;
      };

      const country = inv.dataset.country;
      if (country && country.length === 2) {
        render(country);
      } else if (window.fetch) {
        // Fallback: lightweight IP geo lookup
        fetch('https://ipapi.co/json/')
          .then((r) => r.json())
          .then((data) => render(data.country_code || data.country))
          .catch(() => render(warehouses.find((w) => w.is_default)?.country || warehouses[0].country));
      } else {
        render(warehouses[0].country);
      }
    }

    bindQuantity() {
      this.querySelectorAll('.eb-product-quantity button').forEach((button) => {
        button.addEventListener('click', () => {
          const step = Number(button.dataset.step || 0);
          const current = Number(this.quantityInput?.value || 1);
          if (this.quantityInput) this.quantityInput.value = Math.max(1, current + step);
          this.updateTotals();
        });
      });
      this.quantityInput?.addEventListener('change', () => this.updateTotals());
    }

    totalPrice() {
      const quantity = Number(this.quantityInput?.value || 1);
      const base = Number(this.selectedVariant?.price || 0) * quantity;
      return base;
    }

    saleBadgeLabel(savedCents, percent, forcedMode) {
      const mode = forcedMode || this.dataset.saleBadgeMode || 'percent';
      let label = this.dataset.saleLabel || 'Sale';

      if (mode === 'amount') {
        label = (this.dataset.saveAmountLabel || 'Save __AMOUNT__').replace('__AMOUNT__', money(savedCents, this.currency));
      } else if (mode === 'text') {
        label = this.dataset.saleLabel || 'Sale';
      } else {
        label = (this.dataset.savePercentLabel || 'Save __PERCENT__%').replace('__PERCENT__', String(percent));
      }

      if (label.includes('products.product_card.')) {
        if (mode === 'amount') return `Save ${money(savedCents, this.currency)}`;
        if (mode === 'text') return 'Sale';
        return `Save ${percent}%`;
      }

      return label;
    }

    updatePriceDisplay() {
      const priceWrap = this.querySelector('[data-eb-price-wrap]');
      const variant = this.selectedVariant;
      if (!priceWrap || !variant) return;

      const price = Number(variant.price || 0);
      const comparePrice = Number(variant.compare_at_price || 0);
      const priceNode = priceWrap.querySelector('.eb-price');
      const currentNode = priceWrap.querySelector('.eb-price__current');
      let compareNode = priceWrap.querySelector('.eb-price__compare');
      const badge = priceWrap.querySelector('.eb-save-badge');

      if (currentNode) currentNode.textContent = money(price, this.currency);

      if (comparePrice > price) {
        priceNode?.classList.add('eb-price--sale');
        if (!compareNode && currentNode) {
          compareNode = document.createElement('s');
          compareNode.className = 'eb-price__compare';
          currentNode.insertAdjacentElement('afterend', compareNode);
        }
        if (compareNode) {
          compareNode.hidden = false;
          compareNode.textContent = money(comparePrice, this.currency);
        }
        const saved = comparePrice - price;
        const percent = Math.round((saved * 100) / comparePrice);
        if (badge) {
          badge.textContent = this.saleBadgeLabel(saved, percent);
          badge.hidden = false;
        }
      } else {
        priceNode?.classList.remove('eb-price--sale');
        if (compareNode) compareNode.hidden = true;
        if (badge) badge.hidden = true;
      }
    }

    updateTotals() {
      const total = this.totalPrice();
      const formatted = money(total, this.currency);
      this.updatePriceDisplay();
      if (this.totalNode) this.totalNode.textContent = formatted;
      const variantPrice = Number(this.selectedVariant?.price || 0);
      const variantComparePrice = Number(this.selectedVariant?.compare_at_price || 0);
      this.querySelector('[data-eb-sticky-price]')?.replaceChildren(document.createTextNode(money(variantPrice, this.currency)));
      const stickyCompare = this.querySelector('[data-eb-sticky-compare]');
      if (stickyCompare) {
        const hasComparePrice = variantComparePrice > variantPrice && variantPrice > 0;
        stickyCompare.textContent = hasComparePrice ? money(variantComparePrice, this.currency) : '';
        stickyCompare.hidden = !hasComparePrice;
      }
    }

    bindForm() {
      this.form?.addEventListener('submit', (event) => this.submit(event));
    }

    addCartLines(lines) {
      const items = (lines || []).filter((line) => Number(line.id) > 0 && Number(line.quantity || 0) > 0);
      if (!items.length) return Promise.resolve(null);

      return fetch(this.cartUrl('add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      })
        .then((response) => {
          if (!response.ok) throw response;
          return response.json();
        })
        .then((payload) => {
          const detail = { lines: items, payload, sourceRect: this.rectForEvent(this.atc) };
          document.dispatchEvent(new CustomEvent('eb:cart:add', { detail }));
          return payload;
        });
    }

    submit(event) {
      if (!window.fetch) return;
      event.preventDefault();
      if (this.isAddingToCart || this.atc?.disabled) return;
      const quantity = Number(this.quantityInput?.value || 1);
      const mainId = Number(this.variantSelect?.value);
      const lines = [
        { id: mainId, quantity },
      ].filter((line) => line.id);
      const bundleLines = this.selectedBundleLines();
      const warrantyLine = this.selectedWarrantyLine();
      lines.push(...bundleLines);
      if (warrantyLine) lines.push(warrantyLine);

      this.setStatus('');
      this.setAtcLoading();
      this.addCartLines(lines)
        .then(() => {
          this.setAtcAdded();
        })
        .catch(() => {
          this.setStatus(this.dataset.error || '');
          this.setAtcIdle();
        });
    }

    setStatus(message) {
      if (this.status) this.status.textContent = message;
    }

    setAtcLoading() {
      if (!this.atc) return;
      this.clearAtcTimer();
      this.isAddingToCart = true;
      this.atc.disabled = true;
      this.atc.setAttribute('aria-busy', 'true');
      this.atc.classList.remove('is-added');
      this.atc.classList.add('is-loading');
    }

    setAtcAdded() {
      if (!this.atc) return;
      this.isAddingToCart = false;
      this.atc.removeAttribute('aria-busy');
      this.atc.classList.remove('is-loading');
      this.atc.classList.add('is-added');
      if (this.atcLabel) this.atcLabel.textContent = this.dataset.addedLabel || 'Added';
      this.atcTimer = window.setTimeout(() => this.setAtcIdle(), 1300);
    }

    setAtcIdle() {
      if (!this.atc) return;
      const available = Boolean(this.selectedVariant?.available);
      this.atc.disabled = !available;
      this.atc.removeAttribute('aria-busy');
      this.atc.classList.remove('is-loading', 'is-added');
      if (this.atcLabel) this.atcLabel.textContent = available ? this.dataset.addLabel : this.dataset.soldLabel;
    }

    clearAtcTimer() {
      window.clearTimeout(this.atcTimer);
      this.atcTimer = null;
    }

    rectForEvent(node) {
      const rect = node?.getBoundingClientRect?.();
      if (!rect) return null;
      return {
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
      };
    }

    cartUrl(endpoint) {
      const root = window.Shopify?.routes?.root || '/';
      const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
      return `${normalizedRoot}cart/${endpoint}`;
    }

    bindDrawers() {
      this.drawer = this.querySelector('eb-product-drawer');
      if (!this.boundDrawerCloseCapture) {
        this.boundDrawerCloseCapture = (event) => {
          const close = event.target.closest('[data-eb-drawer-close]');
          if (!close || !this.drawer?.contains(close)) return;
          event.preventDefault();
          event.stopPropagation();
          this.closeDrawer();
        };
        document.addEventListener('click', this.boundDrawerCloseCapture, true);
      }
      this.addEventListener('click', (event) => {
        const open = event.target.closest('[data-eb-drawer-open]');
        if (open) {
          event.preventDefault();
          event.stopPropagation();
          this.openDrawer(open.dataset.ebDrawerOpen, open);
        }
        const close = event.target.closest('[data-eb-drawer-close]');
        if (close) {
          event.preventDefault();
          this.closeDrawer();
        }
      });
      this.drawer?.addEventListener('click', (event) => {
        const close = event.target.closest('[data-eb-drawer-close]');
        if (!close) return;
        event.preventDefault();
        event.stopPropagation();
        this.closeDrawer();
      });
      this.drawer?.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.closeDrawer();
        if (event.key === 'Tab') this.trapDrawerFocus(event);
      });
    }

    openDrawer(id, trigger) {
      const content = this.querySelector(`#${CSS.escape(id)}`);
      if (!content || !this.drawer) return;
      this.drawerTrigger = trigger;
      this.drawer.querySelectorAll('.eb-product-drawer__content').forEach((item) => { item.hidden = true; });
      content.hidden = false;
      const isModal = content.classList.contains('eb-product-drawer__content--modal');
      this.drawer.classList.toggle('is-modal', isModal);
      this.drawer.querySelector('[data-eb-drawer-title]').textContent = content.dataset.drawerTitle || '';
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('eb-product-drawer-open');
      const mainContent = document.getElementById('MainContent');
      if (mainContent && !mainContent.contains(this.drawer)) mainContent.setAttribute('inert', '');
      window.setTimeout(() => this.drawer.querySelector('button, a, input, [tabindex]:not([tabindex="-1"])')?.focus({ preventScroll: true }), 30);
      if (id === 'drawer-test-ride') this.initTestRideMap();
      if (id === 'drawer-compare') this.initCompare();
    }

    closeDrawer() {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('eb-product-drawer-open');
      document.getElementById('MainContent')?.removeAttribute('inert');
      this.drawerTrigger?.focus?.({ preventScroll: true });
    }

    bindBundle() {
      const list = this.querySelector('[data-eb-bundle-list]');
      if (!list) return;
      const min = parseInt(list.dataset.min, 10) || 0;
      // Fixed amount (in cents) taken off the whole bundle once `min` rows are selected.
      const bundleDiscount = parseInt(list.dataset.bundleDiscount, 10) || 0;
      const money = (cents) => this.formatMoney(cents);
      const addButton = this.querySelector('[data-eb-bundle-add]');
      const itemVariantId = (item) => {
        const input = item.querySelector('[data-eb-bundle-variant]');
        return input?.tagName === 'SELECT' ? input.value : input?.value;
      };
      // Fixed amount (in cents) taken off a single accessory row.
      const itemDiscount = (item) => parseInt(item.dataset.itemDiscount, 10) || 0;
      const itemRawPrice = (item) => {
        const sel = item.querySelector('select[data-eb-bundle-variant]');
        if (sel) return parseInt(sel.selectedOptions[0]?.dataset.price, 10) || 0;
        return parseInt(item.dataset.basePrice, 10) || 0;
      };
      const itemNetPrice = (item) => Math.max(0, itemRawPrice(item) - itemDiscount(item));
      const updateBundlePrice = (item, price, comparePrice) => {
        const priceEl = item.querySelector('[data-eb-bundle-price]');
        const compareEl = item.querySelector('[data-eb-bundle-compare]');
        const badgeEl = item.querySelector('[data-eb-bundle-badge]');
        if (priceEl) priceEl.textContent = money(price);
        if (compareEl) {
          compareEl.textContent = money(comparePrice);
          compareEl.hidden = !(comparePrice > price && price > 0);
        }
        if (badgeEl) {
          if (comparePrice > price && price > 0) {
            const saved = comparePrice - price;
            const salePercent = Math.round((saved * 100) / comparePrice);
            badgeEl.textContent = this.saleBadgeLabel(saved, salePercent, 'amount');
            badgeEl.hidden = false;
          } else {
            badgeEl.hidden = true;
          }
        }
      };

      const recalc = () => {
        let count = 0;
        let listPrice = 0;
        let total = 0;
        const selectedIds = new Set();
        list.querySelectorAll('[data-eb-bundle-item]').forEach((item) => {
          if (!item.classList.contains('is-selected')) return;
          count += 1;
          const selectedId = itemVariantId(item);
          if (selectedId) selectedIds.add(String(selectedId));
          listPrice += itemRawPrice(item);
          total += itemNetPrice(item);
        });
        // The bundle amount stacks on top of the per-accessory amounts, but never below zero.
        const bundleOff = min > 0 && count >= min ? Math.min(bundleDiscount, total) : 0;
        const discounted = total - bundleOff;
        const saved = listPrice - discounted;
        const countEl = this.querySelector('[data-eb-bundle-selected]');
        const totalEl = this.querySelector('[data-eb-bundle-total]');
        const wasEl = this.querySelector('[data-eb-bundle-was]');
        const savedEl = this.querySelector('[data-eb-bundle-saved]');
        if (countEl) countEl.textContent = count;
        if (totalEl) totalEl.textContent = money(discounted);
        if (wasEl) {
          wasEl.textContent = money(listPrice);
          wasEl.hidden = saved <= 0;
        }
        if (savedEl) {
          savedEl.textContent = this.bundleSavedLabel(list.dataset.savedLabel, saved);
          savedEl.hidden = saved <= 0;
        }
        this.querySelectorAll('[data-eb-bundle-count]').forEach((el) => { el.textContent = count; });
        this.querySelectorAll('[data-eb-bundle-preview-row]').forEach((row) => {
          const selected = selectedIds.has(String(row.dataset.variantId || ''));
          row.classList.toggle('is-selected', selected);
          row.querySelector('[data-eb-bundle-preview-toggle]')?.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });
        if (addButton) addButton.disabled = count === 0;
      };

      list.querySelectorAll('[data-eb-bundle-item]').forEach((item) => {
        const priceEl = item.querySelector('[data-eb-bundle-price]');
        const sel = item.querySelector('select[data-eb-bundle-variant]');
        if (sel) {
          const initialPrice = parseInt(sel.selectedOptions[0]?.dataset.price, 10) || 0;
          const initialCompare = parseInt(sel.selectedOptions[0]?.dataset.comparePrice, 10) || 0;
          const initialDiscount = itemDiscount(item);
          item.dataset.basePrice = initialPrice;
          item.dataset.baseCompare = initialCompare;
          if (initialDiscount > 0) {
            updateBundlePrice(item, Math.max(0, initialPrice - initialDiscount), Math.max(initialCompare, initialPrice));
          }
          sel.addEventListener('change', () => {
            const p = parseInt(sel.selectedOptions[0]?.dataset.price, 10) || 0;
            const compare = parseInt(sel.selectedOptions[0]?.dataset.comparePrice, 10) || 0;
            const discount = itemDiscount(item);
            item.dataset.basePrice = p;
            item.dataset.baseCompare = compare;
            item.dataset.variantId = sel.value;
            updateBundlePrice(item, Math.max(0, p - discount), discount > 0 ? Math.max(compare, p) : compare);
            recalc();
          });
          sel.addEventListener('click', (e) => e.stopPropagation());
        } else if (priceEl) {
          item.dataset.basePrice = item.dataset.basePrice || 0;
        }
        const toggle = item.querySelector('[data-eb-bundle-toggle]');
        if (toggle) {
          toggle.addEventListener('click', () => {
            item.classList.toggle('is-selected');
            recalc();
          });
        }
        const qv = item.querySelector('[data-eb-bundle-qv]');
        if (qv) {
          qv.addEventListener('click', () => {
            const idx = qv.dataset.ebBundleQv;
            const tpl = item.querySelector(`[data-eb-bundle-qv-content="${idx}"]`);
            if (tpl) this.showBundleQuickView(tpl.innerHTML);
          });
        }
      });
      this.querySelectorAll('[data-eb-bundle-preview-toggle]').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          const row = button.closest('[data-eb-bundle-preview-row]');
          const id = row?.dataset.variantId;
          if (!id) return;
          const target = [...list.querySelectorAll('[data-eb-bundle-item]')]
            .find((item) => String(itemVariantId(item)) === String(id));
          if (!target) return;
          target.classList.toggle('is-selected');
          recalc();
        });
      });
      addButton?.addEventListener('click', () => this.addSelectedBundle());
      recalc();
    }

    bundleSavedLabel(template, savedCents) {
      const amount = this.formatMoney(savedCents);
      if (!template || template.includes('sections.main_product.') || template.includes('translation missing')) {
        return `You save ${amount}`;
      }
      return template.replace('__AMOUNT__', amount);
    }

    selectedBundleLines() {
      const mainId = Number(this.variantSelect?.value);
      return [...this.querySelectorAll('[data-eb-bundle-item].is-selected')].map((item) => {
        const input = item.querySelector('[data-eb-bundle-variant]');
        const variantId = input?.tagName === 'SELECT' ? input.value : input?.value;
        const discount = parseInt(item.dataset.itemDiscount, 10) || 0;
        const properties = {
          _bundle_parent: String(mainId),
          _bundle_type: 'bundle',
        };
        // Read by the automatic discount that applies the bundle savings at checkout.
        if (discount > 0) properties._bundle_item_discount = String(discount);
        return {
          id: Number(variantId),
          quantity: 1,
          properties,
        };
      });
    }

    addSelectedBundle() {
      if (!window.fetch) return;
      const lines = this.selectedBundleLines();
      if (!lines.length) return;
      this.setStatus('');
      // Bundle savings only apply next to the main product, so add it when the cart lacks it.
      this.withParentLine(lines)
        .then((items) => this.addCartLines(items))
        .then(() => this.closeDrawer())
        .catch(() => this.setStatus(this.dataset.error || ''));
    }

    withParentLine(lines) {
      const mainId = Number(this.variantSelect?.value);
      if (!mainId) return Promise.resolve(lines);
      const root = window.Shopify?.routes?.root || '/';
      const cartUrl = `${root.endsWith('/') ? root : `${root}/`}cart.js`;
      return fetch(cartUrl, { headers: { Accept: 'application/json' } })
        .then((response) => {
          if (!response.ok) throw response;
          return response.json();
        })
        .then((cart) => {
          const inCart = (cart?.items || []).some((item) => Number(item.variant_id) === mainId);
          return inCart ? lines : [{ id: mainId, quantity: 1 }, ...lines];
        })
        // If the cart can't be read, add the accessories only rather than risk a duplicate bike.
        .catch(() => lines);
    }

    showBundleQuickView(html) {
      let qvSlot = this.querySelector('#drawer-bundle-qv');
      if (!qvSlot) {
        qvSlot = document.createElement('div');
        qvSlot.className = 'eb-product-drawer__content';
        qvSlot.id = 'drawer-bundle-qv';
        qvSlot.dataset.drawerTitle = 'Quick view';
        this.drawer.querySelector('.eb-product-drawer__body').appendChild(qvSlot);
      }
      qvSlot.innerHTML = `<button type="button" class="eb-qv__back" data-eb-drawer-open="drawer-bundle">&larr;</button>${html}`;
      this.openDrawer('drawer-bundle-qv', this.drawerTrigger);
    }

    bindWarranty() {
      const list = this.querySelector('[data-eb-warranty-list]');
      if (!list) return;
      const coveredWrap = this.querySelector('[data-eb-warranty-covered]');
      const coveredBody = this.querySelector('[data-eb-warranty-covered-body]');
      const defaultCovered = coveredWrap?.dataset.defaultCovered || '';
      const detailSlot = this.querySelector('[data-eb-warranty-detail]');
      const addButton = this.querySelector('[data-eb-warranty-add]');
      const syncWarrantyPreview = (plan) => {
        const selectedId = plan?.dataset.variantId || '';
        this.querySelectorAll('[data-eb-warranty-preview-row]').forEach((row) => {
          const selected = String(row.dataset.variantId || '') === String(selectedId);
          row.classList.toggle('is-selected', selected);
          row.querySelector('[data-eb-warranty-preview-plan]')?.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });
      };
      const swapDetail = (plan) => {
        if (!detailSlot) return;
        if (!plan) {
          detailSlot.innerHTML = '';
          detailSlot.hidden = true;
          return;
        }
        const tpl = plan.nextElementSibling?.matches?.('[data-eb-warranty-detail-content]')
          ? plan.nextElementSibling
          : null;
        const html = tpl?.innerHTML?.trim() || '';
        detailSlot.innerHTML = html;
        detailSlot.hidden = !html;
      };
      const swapCovered = (plan) => {
        if (!coveredWrap || !coveredBody) return;
        if (!plan) {
          coveredBody.innerHTML = '';
          coveredWrap.hidden = true;
          return;
        }
        const html = plan?.dataset.covered || defaultCovered;
        if (html) {
          coveredBody.innerHTML = html;
          coveredWrap.hidden = false;
        } else {
          coveredWrap.hidden = true;
        }
      };
      const clearPlan = () => {
        list.querySelectorAll('[data-eb-warranty-plan]').forEach((p) => p.classList.remove('is-selected'));
        swapDetail(null);
        swapCovered(null);
        syncWarrantyPreview(null);
        if (addButton) addButton.disabled = true;
      };
      const selectPlan = (plan) => {
        if (!plan) return;
        if (plan.classList.contains('is-selected')) {
          clearPlan();
          return;
        }
        list.querySelectorAll('[data-eb-warranty-plan]').forEach((p) => p.classList.remove('is-selected'));
        plan.classList.add('is-selected');
        swapDetail(plan);
        swapCovered(plan);
        syncWarrantyPreview(plan);
        if (addButton) addButton.disabled = false;
      };
      list.querySelectorAll('[data-eb-warranty-plan]').forEach((plan) => {
        plan.addEventListener('click', () => selectPlan(plan));
      });
      this.querySelectorAll('[data-eb-warranty-preview-plan]').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          const row = button.closest('[data-eb-warranty-preview-row]');
          const id = row?.dataset.variantId;
          const plan = [...list.querySelectorAll('[data-eb-warranty-plan]')]
            .find((item) => String(item.dataset.variantId || '') === String(id || ''));
          selectPlan(plan);
        });
      });
      const selected = list.querySelector('[data-eb-warranty-plan].is-selected');
      swapDetail(selected);
      swapCovered(selected);
      syncWarrantyPreview(selected);
      if (addButton) addButton.disabled = !selected;
      addButton?.addEventListener('click', () => this.addSelectedWarranty());
    }

    selectedWarrantyLine() {
      const mainId = Number(this.variantSelect?.value);
      const plan = this.querySelector('[data-eb-warranty-plan].is-selected');
      if (!plan) return null;
      return {
        id: Number(plan.dataset.variantId),
        quantity: 1,
        properties: {
          _bundle_parent: String(mainId),
          _bundle_type: 'warranty',
        },
      };
    }

    addSelectedWarranty() {
      if (!window.fetch) return;
      const line = this.selectedWarrantyLine();
      if (!line) return;
      this.setStatus('');
      this.addCartLines([line])
        .then(() => this.closeDrawer())
        .catch(() => this.setStatus(this.dataset.error || ''));
    }

    formatMoney(cents) {
      const fmt = this.dataset.moneyFormat || '${{amount}}';
      const value = Number(cents || 0) / 100;
      const amount = this.formatNumber(value, '.', ',');
      const amountWithComma = this.formatNumber(value, ',', '.');
      const amountNoDecimals = this.formatNumber(Math.round(value), '.', ',', 0);
      const amountNoDecimalsWithComma = this.formatNumber(Math.round(value), ',', '.', 0);
      const amountWithApostrophe = this.formatNumber(value, '.', "'");
      return fmt
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountWithComma)
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, amountNoDecimalsWithComma)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
        .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, amountWithApostrophe)
        .replace(/\{\{\s*amount\s*\}\}/g, amount);
    }

    formatNumber(value, decimal = '.', thousands = ',', precision = 2) {
      const fixed = Number(value || 0).toFixed(precision);
      const [whole, fraction] = fixed.split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return precision > 0 ? `${grouped}${decimal}${fraction}` : grouped;
    }

    trapDrawerFocus(event) {
      const focusable = [...this.drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')]
        .filter((node) => node.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    bindSizeFit() {
      this.querySelectorAll('[data-eb-size-fit]').forEach((fit) => {
        const bar = fit.querySelector('[data-eb-fit-bar]');
        const handle = fit.querySelector('[data-eb-fit-handle]');
        if (!bar || !handle) return;

        const min = parseFloat(fit.dataset.barMin) || 150;
        const max = parseFloat(fit.dataset.barMax) || 200;
        let height = parseFloat(fit.dataset.start) || Math.round((min + max) / 2);
        let activeSize = 0;

        const sizeForHeight = (h) => {
          const cards = [...fit.querySelectorAll('[data-eb-fit-card]')];
          // Prefer a size whose range contains h; else nearest by midpoint
          let nearest = 0;
          let nearestDist = Infinity;
          for (const card of cards) {
            const lo = parseFloat(card.dataset.min);
            const hi = parseFloat(card.dataset.max);
            const idx = parseInt(card.dataset.sizeIndex, 10);
            if (h >= lo && h <= hi) return idx;
            const mid = (lo + hi) / 2;
            const dist = Math.abs(h - mid);
            if (dist < nearestDist) { nearestDist = dist; nearest = idx; }
          }
          return nearest;
        };

        const inRange = (idx, h) => {
          const card = fit.querySelector(`[data-eb-fit-card][data-size-index="${idx}"]`);
          if (!card) return false;
          return h >= parseFloat(card.dataset.min) && h <= parseFloat(card.dataset.max);
        };

        const render = () => {
          const pct = ((height - min) / (max - min)) * 100;
          handle.style.left = `${Math.max(0, Math.min(100, pct))}%`;
          handle.setAttribute('aria-valuenow', String(Math.round(height)));
          const tip = fit.querySelector('[data-eb-fit-tip]');
          if (tip) tip.textContent = `${Math.round(height)} cm`;

          // active size cards + zones
          fit.querySelectorAll('[data-eb-fit-card]').forEach((c) => {
            c.classList.toggle('is-active', parseInt(c.dataset.sizeIndex, 10) === activeSize);
          });
          fit.querySelectorAll('[data-eb-fit-figure]').forEach((f) => {
            const on = parseInt(f.dataset.sizeIndex, 10) === activeSize;
            f.hidden = !on;
            f.classList.toggle('is-active', on);
          });

          // fit verdict
          const ok = inRange(activeSize, height);
          const tpl = fit.querySelector(`[data-eb-fit-notes="${activeSize}"]`);
          const note = fit.querySelector('[data-eb-fit-note]');
          if (note) {
            const good = tpl?.dataset.good || 'You can stand over the frame comfortably.';
            const bad = tpl?.dataset.bad || "This size may not fit comfortably.";
            note.textContent = ok ? good : bad;
            note.classList.toggle('is-good', ok);
            note.classList.toggle('is-bad', !ok);
          }
        };

        const setHeight = (h, lockSize) => {
          height = Math.max(min, Math.min(max, h));
          if (!lockSize) activeSize = sizeForHeight(height);
          render();
        };

        const pointerToHeight = (clientX) => {
          const r = bar.getBoundingClientRect();
          const p = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
          return min + p * (max - min);
        };

        const onMove = (ev) => {
          const x = ev.touches ? ev.touches[0].clientX : ev.clientX;
          setHeight(pointerToHeight(x));
        };
        const onUp = () => {
          document.removeEventListener('mousemove', onMove);
          document.removeEventListener('mouseup', onUp);
          document.removeEventListener('touchmove', onMove);
          document.removeEventListener('touchend', onUp);
        };
        const onDown = (ev) => {
          ev.preventDefault();
          document.addEventListener('mousemove', onMove);
          document.addEventListener('mouseup', onUp);
          document.addEventListener('touchmove', onMove, { passive: false });
          document.addEventListener('touchend', onUp);
        };
        handle.addEventListener('mousedown', onDown);
        handle.addEventListener('touchstart', onDown, { passive: false });
        bar.addEventListener('click', (ev) => {
          if (ev.target === handle || handle.contains(ev.target)) return;
          setHeight(pointerToHeight(ev.clientX));
        });
        handle.addEventListener('keydown', (ev) => {
          if (ev.key === 'ArrowLeft' || ev.key === 'ArrowDown') { setHeight(height - 1); ev.preventDefault(); }
          if (ev.key === 'ArrowRight' || ev.key === 'ArrowUp') { setHeight(height + 1); ev.preventDefault(); }
        });

        // clicking a size card centres the handle on that size's range
        fit.querySelectorAll('[data-eb-fit-card]').forEach((card) => {
          card.addEventListener('click', () => {
            activeSize = parseInt(card.dataset.sizeIndex, 10);
            const lo = parseFloat(card.dataset.min);
            const hi = parseFloat(card.dataset.max);
            setHeight((lo + hi) / 2, true);
          });
        });

        activeSize = sizeForHeight(height);
        render();
      });
    }

    bindStickyAtc() {
      const sticky = this.querySelector('eb-sticky-atc');
      const button = this.querySelector('[data-eb-atc]');
      if (!sticky || !button || !window.IntersectionObserver) return;
      sticky.querySelector('[data-eb-sticky-submit]')?.addEventListener('click', () => button.click());
      const observer = new IntersectionObserver(([entry]) => {
        sticky.hidden = false;
        sticky.classList.toggle('is-visible', !entry.isIntersecting);
      }, { threshold: 0 });
      observer.observe(button);
    }

    observeSpecGrid() {
      const grid = this.querySelector('[data-eb-spec-grid]');
      if (!grid) return;
      if (!window.IntersectionObserver) {
        grid.classList.add('is-visible');
        return;
      }
      const observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          grid.classList.add('is-visible');
          observer.disconnect();
        }
      }, { threshold: 0.2 });
      observer.observe(grid);
    }
  }

  if (!customElements.get('eb-main-product')) customElements.define('eb-main-product', EbMainProduct);
})();
